<template>
  <v-container class="assortment__container py-1">
    <v-row class="assortment-header mt-1 mb-1">
      {{ $t('scenarioAssortmentPanel.settings') }}
    </v-row>
    <v-divider horizontal class="my-1" />
    <v-row class="my-1">
      <ScenarioOptimiserForm ref="optimiserForm" :read-only="!canChangeOptimiserSettings" />
    </v-row>
    <v-row class="assortment-header mt-1 mb-1">
      {{ $t('scenarioAssortmentPanel.assortments') }}
    </v-row>
    <v-divider horizontal class="my-1" />

    <ProgressBar
      v-if="isRenderingCanvases"
      :message="
        isClusteredSwitchingJobRunning
          ? $t('messages.clusteredSwitchingRunning')
          : $t('messages.canvasesRendering')
      "
      class="pt-5"
    />

    <AgGridVue
      v-if="!isLoadingCanvases"
      class="w-100"
      dom-layout="autoHeight"
      :row-data="assortments"
      :column-defs="headers"
      :grid-options="gridOptions"
      :stop-editing-when-cells-loses-focus="true"
      :row-selection="rowSelection"
      @grid-ready="onGridReady"
      @first-data-rendered="onFirstDataRendered"
      @selection-changed="setSelectedAssortments"
    />

    <ProgressBar
      v-if="isCanvasGenerationJobRunning"
      :percentage="jobStatuses.canvasGeneration.progress"
      :message="$t('messages.inProgress')"
      :status="jobStatuses.canvasGeneration.status"
      class="pb-10"
    />

    <ProgressBar
      v-if="isClScSwitchingJobRunning"
      :percentage="jobStatuses.clScSwitchingMatricesCalculation.progress"
      :message="$t('messages.inProgress')"
      :status="jobStatuses.clScSwitchingMatricesCalculation.status"
      class="pb-10"
    />

    <v-row v-if="!isLoadingCanvases">
      <v-col class="d-flex flex-row justify-end">
        <div v-if="hasClusteredAssortments" class="d-flex flex-grow-1">
          <div v-if="canFetchCanvases" class="d-flex mr-3">
            <v-btn
              variant="outlined"
              :disabled="!isRowSelectable"
              @click="selectRows('clusterId', null, true)"
            >
              {{ tkey('selectAllUnclustered') }}
            </v-btn>
          </div>
          <div v-if="canFetchCanvases" class="d-flex">
            <v-btn
              variant="outlined"
              :disabled="!isRowSelectable"
              @click="selectRows('clusterId', null)"
            >
              {{ tkey('selectAllClustered') }}
            </v-btn>
          </div>
        </div>

        <div class="d-flex">
          <div class="d-flex mr-3">
            <v-btn
              data-id-e2e="btnOptimiseAll"
              data-dd-action-name="OptimiseAllCanvasesBtn"
              :disabled="isOptimiseAllDisabled"
              variant="outlined"
              @click="() => (isOptimiseAllModalOpen = true)"
            >
              {{ tkey('optimiseAllButton') }}
            </v-btn>
            <ErrorTriangle
              v-if="isCanvasConfigInvalidForOptimisation"
              data-id-e2e="errorTriangleOptimiseAll"
              :arrow="false"
              class="ml-2"
              :errors="{
                [$t('assortmentCanvasPage.errorMessages.setOptimiserSettings')]:
                  !isCanvasConfigInvalidForOptimisation,
              }"
            />
          </div>

          <div class="d-flex mr-3">
            <v-btn
              data-id-e2e="btnFetchCanvases"
              variant="outlined"
              :disabled="isFetchCanvasesDisabled"
              @click="runCanvasGenerationJob"
            >
              {{ $t('scenarioAssortmentPanel.fetchCanvases') }}
            </v-btn>
            <ErrorTriangle
              v-if="cannGroupMissingCDT"
              data-id-e2e="errorTriangleFetchCanvases"
              :arrow="false"
              class="ml-2"
              :errors="{
                [$t('assortmentCanvasPage.errorMessages.cannGroupMissingCDT')]:
                  !cannGroupMissingCDT,
              }"
            />
          </div>

          <div v-if="showReporting" class="d-flexmr-3">
            <v-btn
              data-id-e2e="btnViewReport"
              variant="outlined"
              :disabled="isReportingDisabled || isClusteredSwitchingJobRunning"
              :to="reportPageRoute"
            >
              {{ $t('scenarioAssortmentPanel.viewReport') }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="isOptimiseAllModalOpen" :max-width="400">
      <BaseDialogCard
        :title="tkey('optimiseAllModal.title')"
        @close="isOptimiseAllModalOpen = false"
      >
        <div class="text-justify">
          <span>{{ tkey('optimiseAllModal.description') }}</span>
        </div>
        <v-alert
          v-if="enableFinalCheckpoints && finalCanvasCount > 0"
          data-id-e2e="optimiseAllFinalCanvasesWarning"
          type="warning"
          variant="tonal"
          class="mt-3"
        >
          {{ tkey('optimiseAllModal.finalCanvasesWarning', { count: finalCanvasCount }) }}
        </v-alert>
        <div class="d-flex align-center">
          <v-checkbox
            :model-value="createCheckpoint"
            class="rtls-checkbox mx-2"
            @update:model-value="createCheckpoint = $event"
          />
          <span class="mr-4">{{ tkey('optimiseAllModal.createCheckpoint') }}</span>
        </div>
        <template #footer>
          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn action @click="[initOptimisation(), (isOptimiseAllModalOpen = false)]">
                {{ $t('actions.confirm') }}
              </v-btn>
              <v-btn class="ml-2" @click="isOptimiseAllModalOpen = false">
                {{ $t('actions.cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <MicroSpaceOptimisationModal
      v-if="microspaceOptimisationEnabled && selectedAllocationCanvas"
      v-model="isMicrospaceOptimisationModalOpen"
      :canvas="selectedAllocationCanvas"
      :cann-groups="cannGroups"
      :spacebreaks="selectedAllocationSpacebreaks"
      @saved="fetchCanvases"
    />

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import {
  get,
  includes,
  isEqual,
  isEmpty,
  map,
  size,
  reduce,
  keyBy,
  isBoolean,
  some,
  filter,
} from 'lodash';
import jobapi from '@enums/jobapi';
import reportingMetrics from '@enums/reporting-metrics';
import i18n from '@/plugins/vue-i18n';
import agGridUtils from '@/utils/ag-grid-utils';
import agGridIcon from '@/components/AgGridIcon.vue';
import agGridCellRendererComponentWrapper from '@/components/AgGridCellRendererComponentWrapper.vue';
import { usePermission } from '@/composables/use-permission';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { useInputValidations } from '@/composables/input-validations';
import { useFeature } from '@/composables/use-feature';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useScenarioCdtsStore } from '@/stores/scenario-cdts';
import { useClusteringStore } from '@/stores/clustering';
import { useFurnitureStore } from '@/stores/furniture';
import MicroSpaceAllocationButtonRenderer from './components/MicroSpaceAllocationButtonRenderer.vue';
import MicroSpaceOptimisationModal from './MicroSpaceOptimisationModal.vue';

// We need to do translations inside of ag-grid functions where this is not bound to Vue.
const localizationKey = 'assortmentCanvasesPanel';
const t = (key, locKey) => i18n.t(`${locKey}.${key}`);

const iconEnum = {
  goToAssortmentCanvas: 'next',
  notStarted: 'delimiter',
};

const fieldEnum = {
  clusterName: 'clusterName',
  storeClassName: 'storeClassName',
  switchingType: 'switchingType',
  description: 'description',
  generated: 'generated',
  optimised: 'optimised',
  outdated: 'outdatedWarningIcon',
  isFinalCheckpoint: 'isFinalCheckpoint',
};

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    agGridCellRendererComponentWrapper,
    MicroSpaceOptimisationModal,
  },
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },
  props: {
    scenario: {
      required: false,
      type: Object,
      default: null,
    },
  },

  setup() {
    const {
      canChangeOptimiserSettings,
      canOptimiseCanvases,
      canEditCanvasDescription,
      canViewAssortmentCanvas,
      canFetchCanvases,
      canViewReportingPage,
    } = usePermission();

    const { enableFinalCheckpoints, microspaceOptimisationEnabled } = useFeature();

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();
    const { tkey } = useInternationalization(localizationKey);

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    return {
      enableFinalCheckpoints,
      microspaceOptimisationEnabled,
      canChangeOptimiserSettings,
      canOptimiseCanvases,
      canFetchCanvases,
      canViewReportingPage,
      canEditCanvasDescription,
      canViewAssortmentCanvas,
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      tkey,
    };
  },

  data() {
    return {
      gridOptions: {
        columnMenu: 'legacy',
        suppressContextMenu: true,
        defaultColDef: {
          filter: true,
          sortable: true,
          resizable: true,
          minWidth: 80,
          comparator: agGridUtils.sortings.naturalSort,
          menuTabs: ['filterMenuTab'],
          editable: false,
          suppressMovable: true,
          valueParser: agGridUtils.parsers.defaultParser,
        },
        singleClickEdit: true,
        processDataFromClipboard: agGridUtils.utils.processDataFromClipboard,
        processCellFromClipboard: agGridUtils.utils.processCellFromClipboard,
        getRowId: row => {
          const { data } = row;
          return data._id;
        },
        tooltipShowDelay: 0,
        rowHeight: 30,
        headerHeight: 30,
      },
      actionColDef: {
        ...agGridUtils.colDefs.action,
        menuTabs: [],
      },
      createCheckpoint: true,
      assortments: null,
      jobStatusEnums: jobapi.jobStatuses,
      selectedAssortments: [],
      isOptimiseAllModalOpen: false,
      isMicrospaceOptimisationModalOpen: false,
      selectedAllocationCanvas: null,
    };
  },

  computed: {
    ...mapState(useContextStore, ['clientConfig']),
    ...mapState(useScenariosStore, [
      'selectedScenario',
      'jobStatuses',
      'isJobRunning',
      'cannGroups',
    ]),
    ...mapState(useAssortmentCanvasStore, [
      'canvases',
      'getCanvasRenderingStatus',
      'isCanvasConfigInvalidForOptimisation',
      'scenarioCheckpointsByCanvasId',
    ]),
    ...mapState(useScenarioCdtsStore, ['scenarioCdts']),
    ...mapState(useClusteringStore, ['selectedClusterScheme', 'noSelectedClusterScheme']),
    ...mapState(useFurnitureStore, ['getStoreClassesInScenario']),

    rowSelection() {
      return {
        mode: 'multiRow',
        enableClickSelection: true,
        headerCheckbox: this.isRowSelectable,
        selectAll: 'filtered',
        isRowSelectable: () => this.isRowSelectable,
      };
    },

    headers() {
      return [
        {
          headerName: this.tkey('cluster'),
          field: fieldEnum.clusterName,
          colId: fieldEnum.clusterName,
          cellClass: 'bold-text',
          width: 250,
        },
        {
          headerName: this.tkey('storeClass'),
          field: fieldEnum.storeClassName,
          colId: fieldEnum.storeClassName,
          width: 250,
        },
        {
          headerName: this.tkey('switching'),
          field: fieldEnum.switchingType,
          colId: fieldEnum.switchingType,
          width: 200,
          valueGetter: params =>
            !params.data.clusterId || params.data.usesUnclusteredMatrix
              ? this.tkey('unclusteredSwitching')
              : this.tkey('clusteredSwitching'),
        },
        {
          headerName: this.tkey('description'),
          field: fieldEnum.description,
          colId: fieldEnum.description,
          editable: this.isDescriptionEditable,
          flex: 1,
          width: 300,
          cellClassRules: {
            'invalid-not-unique': params => this.isUniqueValidation(params.data.description),
            'disabled-cell': params => params.data.generated !== jobapi.jobStatuses.finished,
          },
          tooltipValueGetter: params => {
            const validationMessage = this.isUniqueValidation(params.value);
            return !isBoolean(validationMessage) ? validationMessage : null;
          },
          onCellValueChanged: params => {
            if (params.newValue !== params.oldValue) {
              const { _id } = params.data;
              const description = params.newValue || '';
              this.save({ _id, description });
            }
          },
        },
        {
          headerName: this.tkey('generated'),
          field: fieldEnum.generated,
          colId: fieldEnum.generated,
          width: 100,
          resizable: false,
          comparator: (valueA, valueB) => {
            return valueB.localeCompare(valueA, undefined, { sensitivity: 'base' });
          },
          cellClass: 'justify-center',
          cellRenderer: agGridIcon,
          cellRendererParams: params => {
            if (!jobapi.jobStatusesIconsMappings[params.data.generated]) {
              return {
                classes: 'v-icon-disabled',
                size: 23,
              };
            }
          },
          // Send the name of the icon using valueGetter. This is used in the component as this.params.value
          valueGetter: params =>
            jobapi.jobStatusesIconsMappings[params.data.generated] || iconEnum.notStarted,
        },
        {
          headerName: this.tkey('optimised'),
          field: fieldEnum.optimised,
          colId: fieldEnum.optimised,
          width: 100,
          resizable: false,
          comparator: (valueA, valueB) => {
            return valueB.localeCompare(valueA, undefined, { sensitivity: 'base' });
          },
          cellRenderer: agGridIcon,
          cellRendererParams: params => {
            const options = { isLoading: this.isCanvasBeingOptimised[params.data._id] };
            if (params.data.optimised) return options;
            return {
              ...options,
              size: 23,
              classes: 'v-icon-disabled',
            };
          },
          valueGetter: params => {
            return params.data.optimised
              ? jobapi.jobStatusesIconsMappings[jobapi.jobStatuses.finished]
              : iconEnum.notStarted;
          },
        },
        ...(this.microspaceOptimisationEnabled
          ? [
              {
                headerName: this.tkey('microspace'),
                colId: 'microspaceOptimisation',
                width: 100,
                resizable: false,
                sortable: false,
                editable: false,
                menuTabs: [],
                cellClass: 'action-column-cell',
                cellRenderer: MicroSpaceAllocationButtonRenderer,
                cellRendererParams: {
                  disabled: !this.canViewAssortmentCanvas,
                  generatedStatus: jobapi.jobStatuses.finished,
                  title: this.$t('scenarioAssortmentPanel.microspaceOptimisation.action'),
                  open: this.openMicrospaceOptimisation,
                },
              },
            ]
          : []),
        ...(this.enableFinalCheckpoints
          ? [
              {
                headerName: this.tkey('finalCheckpoint'),
                field: fieldEnum.isFinalCheckpoint,
                colId: fieldEnum.isFinalCheckpoint,
                width: 50,
                resizable: false,
                comparator: agGridUtils.sortings.naturalSort,
                cellClass: 'justify-center',
                cellRenderer: agGridIcon,
                cellRendererParams: params => ({
                  value: params.data.isFinalCheckpoint ? 'mdi-star' : 'mdi-star-outline',
                  color: 'primary',
                  size: '24px',
                }),
              },
            ]
          : []),
        {
          field: fieldEnum.outdated,
          colId: fieldEnum.outdated,
          headerName: '',
          hide: !this.hasOutdatedCanvases,
          maxWidth: 40,
          sortable: false,
          editable: false,
          menuTabs: [],
          cellClass: 'justify-center warning-icon-wrapper',
          cellRenderer: agGridCellRendererComponentWrapper,
          valueGetter: params => ({
            component: 'error-triangle',
            props: {
              errors: this.getRowWarning(params),
              iconType: 'warning_amber',
              small: true,
              round: true,
            },
          }),
        },
        {
          ...this.actionColDef,
          maxWidth: 40,
          minWidth: 40,
          pinned: 'right',
          cellRenderer: agGridIcon,
          cellRendererParams: params => ({
            to: this.getAssortmentRoute(params.data),
            isDisabled:
              !this.canViewAssortmentCanvas ||
              params.data.generated !== jobapi.jobStatuses.finished,
            size: 28,
            classes: 'v-icon-blue',
          }),
          cellClass: 'action-column-cell',
          valueGetter: params => params.data.goToAssortmentCanvas,
        },
      ];
    },

    hasDataChanges() {
      return this.$refs.optimiserForm.hasDataChanges;
    },

    hasClusteredAssortments() {
      return some(this.assortments, a => !!a.clusterId);
    },

    isFetchCanvasesDisabled() {
      return (
        this.isAssortmentOptimisationJobRunning ||
        this.cannGroupMissingCDT ||
        !this.canFetchCanvases ||
        !size(this.selectedAssortments)
      );
    },

    cannGroupMissingCDT() {
      const cdtCannGroupIds = this.scenarioCdts.filter(v => v.selected).map(v => v.cannGroupId);
      const cannGroupsMissingCdts = this.cannGroups.filter(cannGroup => {
        return !cdtCannGroupIds.includes(cannGroup.key);
      });
      return size(cannGroupsMissingCdts) > 0;
    },

    currentScenario() {
      return this.scenario || this.selectedScenario;
    },

    isCanvasGenerationJobRunning() {
      return this.isJobRunning('canvasGeneration');
    },

    isClusteredSwitchingJobRunning() {
      return this.isJobRunning('clusteredSwitching');
    },

    isAssortmentOptimisationJobRunning() {
      return this.canvases.some(canvas => {
        const canvasStatus = get(canvas, 'jobs.assortmentOptimisation.status', '').toLowerCase();

        return isEmpty(canvasStatus) ? false : !includes(jobapi.jobFinishedStatuses, canvasStatus);
      });
    },

    isClScSwitchingJobRunning() {
      return this.isJobRunning('clScSwitchingMatricesCalculation');
    },

    isReportingDisabled() {
      return !this.canViewReportingPage;
    },

    canvasesByClusterStoreClass() {
      return keyBy(this.canvases, c => `${c.clusterId}-${c.storeClassId}`);
    },

    isCanvasBeingOptimised() {
      return this.canvases.reduce(
        (acc, c) => ({
          ...acc,
          [c._id]: jobapi.isJobInProgress(get(c, 'jobs.assortmentOptimisation.status', '')),
        }),
        {}
      );
    },

    isOptimiseAllDisabled() {
      return (
        this.isClusteredSwitchingJobRunning ||
        this.isAssortmentOptimisationJobRunning ||
        this.isCanvasConfigInvalidForOptimisation ||
        !this.canvases.length ||
        !this.canOptimiseCanvases
      );
    },

    finalCanvasCount() {
      return filter(this.canvases, c => get(c, fieldEnum.isFinalCheckpoint, false)).length;
    },

    nonFinalCanvasIds() {
      return map(
        filter(this.canvases, c => !get(c, fieldEnum.isFinalCheckpoint, false)),
        '_id'
      );
    },

    isRowSelectable() {
      return !this.isAssortmentOptimisationJobRunning && this.canFetchCanvases;
    },

    showReporting() {
      return get(this.clientConfig, 'features.reportingControlsEnabled');
    },

    isLoadingCanvases() {
      return (
        this.getCanvasRenderingStatus ||
        this.isCanvasGenerationJobRunning ||
        this.isClusteredSwitchingJobRunning ||
        this.isClScSwitchingJobRunning
      );
    },

    isRenderingCanvases() {
      return (
        !this.isCanvasGenerationJobRunning &&
        !this.isClScSwitchingJobRunning &&
        (this.getCanvasRenderingStatus || this.isClusteredSwitchingJobRunning)
      );
    },

    reportPageRoute() {
      const firstCanvas = get(this.canvases[0], '_id', null);
      const firstCheckpoint = get(
        this.scenarioCheckpointsByCanvasId,
        [firstCanvas, 0, '_id'],
        null
      );
      return {
        path: '/reporting-main',
        query: {
          workpackageIds: [this.currentScenario.workpackageId],
          baseline: [firstCheckpoint],
          metricValue: reportingMetrics.metrics.sales,
        },
      };
    },

    hasOutdatedCanvases() {
      return some(this.canvases, 'outdated');
    },

    selectedAllocationSpacebreaks() {
      return (
        this.getStoreClassesInScenario.find(
          storeClass => storeClass._id === this.selectedAllocationCanvas?.storeClassId
        )?.spacebreaks || []
      );
    },
  },

  watch: {
    isClusteredSwitchingJobRunning(newValue) {
      const clusters =
        newValue || this.noSelectedClusterScheme ? [] : this.selectedClusterScheme.clusters;
      this.assortments = this.generateAssortments(clusters);
    },

    isAssortmentOptimisationJobRunning() {
      if (!this.gridApi) return;
      this.refreshAssortments();
    },

    canvases(newValue, oldValue) {
      if (!this.gridApi || isEqual(newValue, oldValue)) return;
      this.refreshAssortments();
    },

    async isCanvasGenerationJobRunning(newValue) {
      // Make sure that the assortment data is correct if canvas generation job fails
      if (!newValue && this.jobStatuses.canvasGeneration.status === jobapi.jobStatuses.failed) {
        await this.fetchCanvases();
        this.refreshAssortments();
      }
    },
  },

  async created() {
    await this.init();
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'fetchCanvas',
      'fetchCanvases',
      'fetchScenarioCheckpoints',
      'runCanvasGeneration',
      'updateAssortmentCanvas',
      'changeCanvasRenderingStatus',
      'runAssortmentOptimisation',
    ]),
    ...mapActions(useScenariosStore, ['loadScenario', 'updateScenario']),
    ...mapActions(useScenarioCdtsStore, ['fetchScenarioCdts']),
    ...mapActions(useClusteringStore, ['fetchScenarioClusters']),
    ...mapActions(useFurnitureStore, ['fetchScenarioFurniture']),

    onGridReady(params) {
      this.gridApi = params.api;
    },

    onFirstDataRendered() {
      if (size(this.selectedAssortments)) this.selectedAssortments = [];
      else this.selectRows(fieldEnum.generated, jobapi.jobStatuses.finished);
    },

    isDescriptionEditable(params) {
      return params.data.generated === jobapi.jobStatuses.finished && this.canEditCanvasDescription;
    },

    isUniqueValidation(value) {
      const description = value;
      const descriptionIsDuplicated = size(filter(this.assortments, { description })) > 1;
      // Allow multiple empty values
      if (!isEmpty(value) && descriptionIsDuplicated) {
        return this.$t('validationErrors.unique', [this.$t('scenarioAssortmentPanel.description')]);
      }
      return false;
    },

    getRowWarning(params) {
      return {
        // need to negate, as evaluated false means it's outdated
        [this.tkey('errorMessages.outdatedWarning')]: !get(params.data, 'outdated'),
      };
    },

    async init() {
      // cann groups seem to always be loaded, but if navigating to component using url cdts may be empty
      await Promise.all([
        this.fetchCanvases(),
        this.fetchScenarioCheckpoints(),
        this.fetchScenarioFurniture(),
        this.fetchScenarioClusters(),
        this.fetchScenarioCdts(),
      ]);
      // If clustered switching job is running, remove clustered assortment rows
      // If clustered switching job has finished, we must have a selected scheme to generate clustered rows
      const clusters =
        this.isClusteredSwitchingJobRunning || this.noSelectedClusterScheme
          ? []
          : this.selectedClusterScheme.clusters;
      this.assortments = this.generateAssortments(clusters);
      this.changeCanvasRenderingStatus(false);
    },

    generateAssortments(clusters) {
      const storeClasses = this.getStoreClassesInScenario;
      const unclustered = { clusterId: null, clusterName: t('unClustered', localizationKey) };

      return reduce(
        [unclustered, ...clusters],
        (acc, c) => {
          if (c.isNoDataCluster) return acc;
          const combinations = map(storeClasses, sc => {
            const key = `${c.clusterId}-${sc._id}`;
            const canvas = this.canvasesByClusterStoreClass[key];

            return {
              _id: get(canvas, '_id', key),
              clusterId: c.clusterId,
              clusterName: c.clusterName,
              storeClassId: sc._id,
              storeClassName: sc.name,
              usesUnclusteredMatrix: c.usesUnclusteredMatrix,
              description: get(canvas, 'description', ''),
              generated: canvas ? jobapi.jobStatuses.finished : iconEnum.notStarted,
              optimised: get(canvas, 'hasBeenOptimised', false),
              outdated: get(canvas, 'outdated', false),
              goToAssortmentCanvas: iconEnum.goToAssortmentCanvas,
              isFinalCheckpoint: get(canvas, fieldEnum.isFinalCheckpoint, false),
            };
          });
          acc.push(...combinations);
          return acc;
        },
        []
      );
    },

    setSelectedAssortments() {
      const selectedNodes = this.gridApi.getSelectedNodes();
      this.selectedAssortments = selectedNodes.map(node => ({
        clusterId: node.data.clusterId,
        storeClassId: node.data.storeClassId,
      }));
    },

    selectRows(column, value, equalTo = false) {
      // Select all rows by default if canvases have not been generated
      this.gridApi.forEachNode(node => {
        const selectNode = equalTo ? node.data[column] === value : node.data[column] !== value;
        node.setSelected(selectNode);
      });
    },

    async save(item) {
      if (!isBoolean(this.isUniqueValidation(item.description))) return false;

      await this.updateAssortmentCanvas({
        canvas: {
          _id: item._id,
          description: item.description,
        },
      });
    },

    async runCanvasGenerationJob() {
      const scenarioId = this.selectedScenario._id;
      await this.runCanvasGeneration({
        scenarioId,
        selectedCanvases: this.selectedAssortments,
      });
      await this.loadScenario(scenarioId);
      await this.fetchCanvases();
      this.refreshAssortments();
    },

    getAssortmentRoute(row) {
      return {
        name: 'assortments-canvas',
        params: { scenarioId: this.currentScenario._id, canvasId: row._id },
      };
    },

    openMicrospaceOptimisation(row) {
      if (
        !this.microspaceOptimisationEnabled ||
        row.generated !== jobapi.jobStatuses.finished ||
        !this.canViewAssortmentCanvas
      )
        return;
      this.selectedAllocationCanvas = {
        _id: row._id,
        scenarioId: this.currentScenario._id,
        storeClassId: row.storeClassId,
      };
      this.isMicrospaceOptimisationModalOpen = true;
    },

    async initOptimisation() {
      const payload = {
        scenarioId: this.selectedScenario._id,
        canvasIds: this.enableFinalCheckpoints ? this.nonFinalCanvasIds : map(this.canvases, '_id'),
        useFurniture: true,
        createCheckpoint: this.createCheckpoint,
      };

      await this.runAssortmentOptimisation(payload);
      await this.fetchCanvases();
    },

    refreshAssortments() {
      // Update assortment data with latest canvas data
      this.assortments = map(this.assortments, a => {
        const key = `${a.clusterId}-${a.storeClassId}`;
        const canvas = this.canvasesByClusterStoreClass[key];
        return {
          ...a,
          _id: get(canvas, '_id', key),
          description: get(canvas, 'description', ''),
          generated: canvas ? jobapi.jobStatuses.finished : iconEnum.notStarted,
          optimised: get(canvas, 'hasBeenOptimised', false),
          outdated: get(canvas, 'outdated', false),
          isFinalCheckpoint: get(canvas, fieldEnum.isFinalCheckpoint, false),
        };
      });

      this.gridApi.refreshCells({
        columns: [
          fieldEnum.generated,
          fieldEnum.optimised,
          fieldEnum.outdated,
          fieldEnum.description,
          fieldEnum.isFinalCheckpoint,
        ],
        force: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.assortment-header {
  font-weight: bold;
  font-size: 1.2rem;
}

.disabled-cell {
  background-color: $assortment-draggable-disabled-color;
}

.assortment {
  &__container {
    background: $assortment-input-background;
    padding: 0 auto;
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    max-width: none;
  }
}

:deep(.invalid-not-unique) {
  border-bottom: 2px solid $assortment-table-error-cell-border !important;
}

:deep(.ag-theme-alpine) {
  .ag-header-select-all {
    margin-right: 13px;
  }

  .ag-row-selected {
    &.ag-row-even {
      background: $assortment-table-blue-bg-colour !important;
    }

    &.ag-row-odd {
      background: $assortment-table-white-bg-colour !important;
    }

    &:before {
      content: none;
    }
  }

  .ag-cell-range-selected:not(.ag-cell-focus) {
    background-color: inherit !important;
  }

  .ag-has-focus .ag-cell-focus.ag-cell {
    padding-left: 5px;
  }
}

:deep(.v-icon) {
  &.v-icon-blue {
    color: $assortment-action-icon-color !important;
  }

  &.v-icon-disabled {
    color: $assortment-radio-button-colour !important;
  }
}

:deep(.warning-icon-wrapper i) {
  color: $assortment-warning-alert-border;
}
</style>
